import { useCallback, useMemo } from 'react'
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom'
import { roundOf } from '@/utils/methods/number'
import { useTranslations } from '@/core/translation'
import { useRemoveTicketMutation } from '@/redux/package'
import { useToast } from '@/components'
import { ROUTES, TICKET_FORMATS, TicketFormats, usePackageInfo, isEventDateTimeTbd } from '@/utils'
import useUpdatePackageInfo from '@/utils/hooks/useUpdatePackageInfo'
import { useAnalytics, CART_TYPE } from '@/core/analytics'
import { useGetDefaultMapPins } from '../../../Map'

export const useEventDetailsController = () => {
  const { currentPackage, isTicketModify, isSingleProduct } = usePackageInfo()
  const [removeTicket] = useRemoveTicketMutation()
  const { refetchPackageInfo } = useUpdatePackageInfo()

  const t = useTranslations()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const analytics = useAnalytics()

  const { sessionId } = currentPackage ?? { sessionId: '' }
  const { id: eventId, airport, cityCenter, venue } = currentPackage?.event ?? {}

  const { pins } = useGetDefaultMapPins({ airport, cityCenter, venue })

  const eventDetails = useMemo(() => {
    if (!currentPackage) return {}

    const { event, ticketInfo, cartInfo } = currentPackage

    const { street, city, region, postalCode } = event.venue.address

    const mobileTicketFormats: TicketFormats[] = [
      TICKET_FORMATS.MOBILE_TICKET,
      TICKET_FORMATS.E_TICKET,
    ]

    let ticket = null

    if (cartInfo?.ticket)
      ticket = {
        quantity: cartInfo.ticket.quantity,
        details: `${ticketInfo?.sectionDisplay} · ${t('tickets.list.row', { row: ticketInfo?.rowDisplay ?? '' })}`,
        isMobileTicket: ticketInfo?.format && mobileTicketFormats.includes(ticketInfo?.format),
        isAccessible: ticketInfo?.wheelchairAccessible,
        isInstantDelivery: ticketInfo?.isInstantDelivery,
      }

    const { isDateTbd, isTimeTbd } = isEventDateTimeTbd(event.timings.time, event.name)

    return {
      event: {
        name: event.name,
        time: isDateTbd
          ? t('eventList.tbd')
          : `${event.timings.day} ${event.timings.month} ${event.timings.date} ${t('header.event.timingAt')} ${isTimeTbd ? t('eventList.tbd') : event.timings.time}`,
      },
      ticket,
      location: {
        name: event.venue.name,
        address: `${street}, ${city}, ${region} ${postalCode}`,
        fromAirport: `${roundOf(airport?.distance ?? 0)} ${t('general.miles')} ${t('general.from')} ${airport?.name} (${airport?.code})`,
        fromCityCenter: `${roundOf(cityCenter?.distance ?? 0)} ${t('general.miles')} ${t('general.from')} ${cityCenter?.name}, ${cityCenter?.province}, ${cityCenter?.country}`,
      },
    }
  }, [
    currentPackage,
    t,
    airport?.distance,
    airport?.name,
    airport?.code,
    cityCenter?.distance,
    cityCenter?.name,
    cityCenter?.province,
    cityCenter?.country,
  ])

  const centerLocation = useMemo(
    () => ({ lat: venue?.address?.latitude ?? 0, lng: venue?.address?.longitude ?? 0 }),
    [venue?.address?.latitude, venue?.address?.longitude],
  )

  const onTicketEditClick = useCallback(() => {
    navigate({
      pathname: ROUTES.PACKAGE.TICKET_SELECT.replace('{packageId}', eventId || ''),
      search: createSearchParams({
        sessionId,
        modify: 'true',
        redirectUrl: location.pathname,
      }).toString(),
    })
  }, [eventId, sessionId, navigate, location.pathname])

  const onTicketRemoveClick = useCallback(async () => {
    try {
      await removeTicket({ sessionId }).unwrap()
      refetchPackageInfo(sessionId)
      analytics.sendRemoveFromCartEvent(CART_TYPE.TICKET)
    } catch {
      toast(t('general.apiError'), 'error')
    }
  }, [removeTicket, sessionId, refetchPackageInfo, analytics, toast, t])

  return {
    locations: pins,
    isEditActionDisabled: isTicketModify,
    isSingleProduct,
    eventDetails,
    centerLocation,
    onTicketEditClick,
    onTicketRemoveClick,
  }
}
